import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  User, 
  MapPin, 
  Calendar, 
  Clock, 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  MessageCircle, 
  Phone,
  Star,
  Users,
  Car,
  Filter,
  Search
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ConnectRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Dummy connect requests data
  const dummyRequests = [
    {
      id: 1,
      user: {
        name: 'Priya Sharma',
        rating: 4.8,
        rides: 45,
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43210'
      },
      ride: {
        from: 'Mumbai Central',
        to: 'Andheri West',
        date: '2024-01-15',
        time: '09:00 AM',
        price: 120,
        vehicle: 'Honda City'
      },
      status: 'pending',
      message: 'Hi! I\'m interested in your ride. I\'m a regular commuter and would love to join.',
      requestedAt: '2024-01-14T10:30:00Z',
      seats: 1
    },
    {
      id: 2,
      user: {
        name: 'Rahul Mehta',
        rating: 4.6,
        rides: 32,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43211'
      },
      ride: {
        from: 'Bandra West',
        to: 'Powai',
        date: '2024-01-15',
        time: '08:30 AM',
        price: 150,
        vehicle: 'Maruti Swift'
      },
      status: 'accepted',
      message: 'Perfect timing for me! I work in Powai and this would be very convenient.',
      requestedAt: '2024-01-14T09:15:00Z',
      seats: 1
    },
    {
      id: 3,
      user: {
        name: 'Anjali Patel',
        rating: 4.9,
        rides: 67,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43212'
      },
      ride: {
        from: 'Thane',
        to: 'BKC',
        date: '2024-01-16',
        time: '07:45 AM',
        price: 200,
        vehicle: 'Hyundai i20'
      },
      status: 'rejected',
      message: 'I need 2 seats for me and my colleague. Is that possible?',
      requestedAt: '2024-01-14T14:20:00Z',
      seats: 2
    },
    {
      id: 4,
      user: {
        name: 'Vikram Singh',
        rating: 4.7,
        rides: 28,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43213'
      },
      ride: {
        from: 'Navi Mumbai',
        to: 'Fort',
        date: '2024-01-15',
        time: '10:00 AM',
        price: 180,
        vehicle: 'Tata Nexon'
      },
      status: 'pending',
      message: 'I\'m flexible with timing. Can we discuss the pickup point?',
      requestedAt: '2024-01-14T16:45:00Z',
      seats: 1
    },
    {
      id: 5,
      user: {
        name: 'Sneha Reddy',
        rating: 4.5,
        rides: 19,
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43214'
      },
      ride: {
        from: 'Kurla',
        to: 'Worli',
        date: '2024-01-16',
        time: '08:15 AM',
        price: 100,
        vehicle: 'Maruti Alto'
      },
      status: 'pending',
      message: 'Student here! This would really help me save on transport costs.',
      requestedAt: '2024-01-14T18:30:00Z',
      seats: 1
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setRequests(dummyRequests);
      setFilteredRequests(dummyRequests);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = requests;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(request =>
        request.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.ride.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.ride.to.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(request => request.status === selectedFilter);
    }

    setFilteredRequests(filtered);
  }, [searchTerm, selectedFilter, requests]);

  const handleAccept = (requestId) => {
    setRequests(prev => prev.map(request => 
      request.id === requestId ? { ...request, status: 'accepted' } : request
    ));
    alert('Request accepted! The user will be notified.');
  };

  const handleReject = (requestId) => {
    setRequests(prev => prev.map(request => 
      request.id === requestId ? { ...request, status: 'rejected' } : request
    ));
    alert('Request rejected. The user will be notified.');
  };

  const handleCall = (phone) => {
    alert(`Calling ${phone}`);
  };

  const handleMessage = (userName) => {
    alert(`Opening chat with ${userName}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'accepted': return '✅';
      case 'rejected': return '❌';
      default: return '📋';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const requestTime = new Date(dateString);
    const diffInHours = Math.floor((now - requestTime) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  };

  const getRequestCount = (status) => {
    return requests.filter(request => request.status === status).length;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Connect Requests</h1>
        <p className="text-gray-600">Manage ride connection requests from other users</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{requests.length}</p>
            </div>
            <Bell className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{getRequestCount('pending')}</p>
            </div>
            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <span className="text-yellow-600">⏳</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-green-600">{getRequestCount('accepted')}</p>
            </div>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600">✅</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{getRequestCount('rejected')}</p>
            </div>
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600">❌</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Requests</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Requests List */}
      <div className="space-y-6">
        {filteredRequests.map((request) => (
          <div key={request.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <img
                    src={request.user.avatar}
                    alt={request.user.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{request.user.name}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span>{request.user.rating}</span>
                      </div>
                      <span>({request.user.rides} rides)</span>
                      <span>{formatTimeAgo(request.requestedAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                    {getStatusIcon(request.status)} {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </span>
                </div>
              </div>

              {/* Ride Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <span className="font-medium">{request.ride.from} → {request.ride.to}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <span>{request.ride.date} at {request.ride.time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span>₹{request.ride.price} per seat</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Car className="w-4 h-4 text-gray-500" />
                    <span>{request.ride.vehicle}</span>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <p className="text-gray-700 bg-blue-50 rounded-lg p-3">
                  "{request.message}"
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    Requesting {request.seats} seat{request.seats !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {request.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAccept(request.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept
                      </button>
                      <button
                        onClick={() => handleReject(request.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleCall(request.user.phone)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Call"
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleMessage(request.user.name)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Message"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No requests found</h3>
          <p className="text-gray-600">
            {searchTerm || selectedFilter !== 'all' 
              ? 'Try adjusting your search criteria.' 
              : 'You don\'t have any connect requests yet.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default ConnectRequests; 